/** Standard dialog recipe — one shape for every console dialog:
 *  title row with an ✕ close button, content, and right-aligned actions
 *  (Cancel first, primary action last; destructive actions use
 *  `color="error"`). Sizes: 'sm' for simple forms, 'md' for wizards/tabbed
 *  dialogs, 'lg' only for data-heavy surfaces. Use for NEW dialogs; migrate
 *  existing ones opportunistically (reference: TripTypeCreateDialog). */
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
} from '@mui/material';
import type { ReactNode } from 'react';

export function AppDialog({
  open,
  onClose,
  title,
  size = 'sm',
  children,
  actions,
  disableBackdropClose = false,
  closeDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  /** Right-aligned row: Cancel first, primary last. Omit for info dialogs. */
  actions?: ReactNode;
  /** Keep backdrop/Escape from closing (dirty forms); the ✕ still closes. */
  disableBackdropClose?: boolean;
  /** Nothing closes it — the ✕ is disabled too — while something must be waited out (a
   *  revert running in the background). */
  closeDisabled?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onClose={disableBackdropClose || closeDisabled ? undefined : onClose}
      fullWidth
      maxWidth={size}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{title}</Box>
        <IconButton aria-label="Close" size="small" onClick={onClose} disabled={closeDisabled}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}
